<template>
    <q-layout view="hHh lpR fFf">
        <marketing-nav :mobile-nav-open="mobileNav" @toggle-mobile="mobileNav = !mobileNav" />

        <q-page-container>
            <!-- ============================================================
                 HERO - code sample preserved from the old Developers page
                 ============================================================ -->
            <section class="mkt-hero-light" style="padding:80px 0 60px;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-xl items-center">
                        <div class="col-12 col-md-5">
                            <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;">
                                <q-icon name="code" class="mkt-trust-icon" /> API-first &middot; MCP-native
                            </span>
                            <h1 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.8rem,3.5vw,2.6rem);line-height:1.15;color:#0F172A;">
                                Build signing into <span class="text-gradient-indigo">your app</span> or agent.
                                <span class="visually-hidden"> - eSign Launchpad developer API, MCP server, and language SDKs</span>
                            </h1>
                            <p class="q-mb-lg" style="font-size:1.05rem;line-height:1.7;color:#475569;">
                                Integrate e-signatures with our REST API, embed signing in your product, or let a Claude-native
                                agent drive the flow via our MCP server. Starting at <b>$49/mo</b>.
                            </p>
                            <div class="row q-gutter-md mkt-hero-cta-row">
                                <q-btn color="deep-purple" size="lg" label="Start Building" href="/Account/Register?plan=developer"
                                    no-caps unelevated style="border-radius:10px;padding:10px 28px;" />
                                <q-btn outline size="lg" no-caps href="/Docs/ApiReference"
                                    style="border-radius:10px;padding:10px 28px;color:#334155;border-color:#CBD5E1;">
                                    API Reference
                                </q-btn>
                            </div>
                        </div>
                        <div class="col-12 col-md-7">
                            <div class="mkt-code-block">
<pre style="margin:0;white-space:pre;overflow-x:auto;"><span class="code-comment"># Send a document for signature</span>
<span class="code-method">curl</span> <span class="code-url">-X POST</span> <span class="code-string">https://api.esignlaunchpad.com/api/v1/packages</span> \
  <span class="code-url">-H</span> <span class="code-string">"Authorization: Bearer $ESIGN_TOKEN"</span> \
  <span class="code-url">-H</span> <span class="code-string">"Content-Type: application/json"</span> \
  <span class="code-url">-d</span> <span class="code-string">'{
    "name": "Service Agreement",
    "signers": [{
      "email": "signer@example.com",
      "name": "Jane Doe",
      "role": "Signer"
    }],
    "send": true
  }'</span></pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Developer-surface spotlight: visual recap of the four
                 integration paths (REST, webhooks, iframe, SDKs) right
                 below the hero. Sets the page's mental model before
                 visitors dive into use cases and quick-start. -->
            <marketing-spotlight
                slug="spot-developer-api"
                alt="Developer surfaces - REST API, signed webhooks, iframe embedded signing, and language SDKs across 8 languages"
                eyebrow-icon="code"
                icon-circle-class="mkt-icon-circle--blue"
                title="Four ways to integrate"
                blurb="REST for the synchronous calls, signed webhooks for the async events, an iframe for embedded signing inside your product, and SDKs in eight languages for everything in between."
                :bullets="['22+ REST API endpoints with idempotency keys', '24 webhook events, HMAC-SHA256 signed', 'Embedded signing iframe with postMessage events', 'SDKs in C#, TypeScript, Python, PHP, Node, Java, Go, Ruby']"
                cta-label="Read the API reference"
                cta-href="/Docs/ApiReference"
                flip />

            <!-- ============================================================
                 USE CASES - from absorbed ForDeveloper persona page
                 ============================================================ -->
            <section id="use-cases" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Use cases</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Who builds with eSign Launchpad</h2>
                        <p class="text-grey-7" style="font-size:1.05rem;max-width:600px;margin:0 auto;">
                            Indie SaaS founders, ISVs, and AI agent builders. Self-serve from $49/mo.
                        </p>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-4" v-for="uc in useCases" :key="uc.title">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="uc.icon" size="36px" :style="`color:${uc.color};`" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.1rem;color:#0a0f1a;line-height:1.25;">{{ uc.title }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <p class="text-grey-7 q-mb-none" style="font-size:0.95rem;line-height:1.6;">{{ uc.detail }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 FEATURES - Quick Start + API + Webhooks + SDKs + MCP
                 ============================================================ -->
            <section id="features" class="mkt-light reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Features</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Three steps to your first API signature</h2>
                    </div>
                    <div class="row q-col-gutter-xl">
                        <div class="col-12 col-md-4" v-for="step in quickStart" :key="step.number">
                            <div class="text-center">
                                <div class="mkt-step-number q-mb-md">{{ step.number }}</div>
                                <h3 class="text-weight-bold q-mb-sm" style="font-size:1.1rem;color:#0a0f1a;">{{ step.title }}</h3>
                                <p class="text-grey-7" style="font-size:0.9rem;line-height:1.6;">{{ step.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- AI-agents spotlight: visual lead-in to the MCP section.
                 The MCP section itself keeps the code-block-on-the-right
                 layout (the actual Claude Desktop config is load-bearing
                 for developers); this band introduces the concept first. -->
            <marketing-spotlight
                slug="spot-ai-agents"
                alt="eSign Launchpad MCP server lets Claude and other AI agents drive signing flows with 90+ tools"
                eyebrow-icon="auto_awesome"
                icon-circle-class="mkt-icon-circle--gold"
                title="Your AI agent can run your signing flows"
                blurb="Drop our Model Context Protocol server into Claude Desktop, Cline, Continue, or any MCP client. 90+ tools - the same surface as our REST API - drivable from natural language."
                :bullets="['90+ MCP tools, scoped per credential', 'Same pricing as REST - agent calls billed identically', 'Works with Claude Desktop and any MCP host', 'Send packages, add signers, void docs - all from a prompt']"
                cta-label="See the MCP config"
                cta-href="#mcp" />

            <!-- ============================================================
                 MCP - new: AI agent differentiator
                 ============================================================ -->
            <section id="mcp" class="mkt-white reveal-fade-left" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-xl items-center">
                        <div class="col-12 col-md-5">
                            <span class="mkt-trust-pill mkt-trust-pill--light q-mb-md" style="display:inline-block;background:#F5F3FF;">
                                <q-icon name="auto_awesome" class="mkt-trust-icon" style="color:#7C3AED;" /> New in 2026
                            </span>
                            <h2 class="text-weight-bold q-mb-sm" style="font-size:1.6rem;color:#0a0f1a;">
                                MCP server for <span class="text-gradient-indigo">Claude-native</span> workflows.
                            </h2>
                            <p class="text-grey-7 q-mb-md" style="font-size:1rem;line-height:1.7;">
                                Drive signing packages directly from Claude Desktop or any Model Context Protocol
                                client. Our MCP server exposes the same 22+ endpoints as our REST API, so agents can
                                upload documents, add signers, and send for signature without a human in the loop.
                            </p>
                            <q-list dense class="mkt-feature-list q-mb-md">
                                <q-item v-for="f in mcpBullets" :key="f" class="q-px-none">
                                    <q-item-section avatar top style="min-width:26px;padding-right:0;">
                                        <q-icon name="check_circle" color="positive" size="18px" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label style="font-size:0.9rem;color:#333;line-height:1.55;">{{ f }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                            <q-btn outline no-caps label="MCP integration docs" href="/Docs/IntegrationGuides#mcp"
                                style="border-radius:8px;color:#7C3AED;border-color:#DDD6FE;" />
                        </div>
                        <div class="col-12 col-md-7">
                            <div class="mkt-code-block">
<pre style="margin:0;white-space:pre;overflow-x:auto;"><span class="code-comment"># Claude Desktop config - claude_desktop_config.json</span>
{
  <span class="code-string">"mcpServers"</span>: {
    <span class="code-string">"esign-launchpad"</span>: {
      <span class="code-string">"command"</span>: <span class="code-string">"npx"</span>,
      <span class="code-string">"args"</span>: [<span class="code-string">"-y"</span>, <span class="code-string">"@esign-launchpad/mcp-server"</span>],
      <span class="code-string">"env"</span>: {
        <span class="code-string">"ESIGN_TOKEN"</span>: <span class="code-string">"pat_your_personal_access_token"</span>
      }
    }
  }
}</pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- API Endpoints -->
            <section class="mkt-light reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="text-center q-mb-xl">
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:1.5rem;color:#0a0f1a;">22+ API Endpoints</h2>
                        <p class="text-grey-7" style="font-size:1rem;">Full control over the signing lifecycle</p>
                    </div>
                    <div class="row q-col-gutter-lg">
                        <div class="col-12 col-md-4" v-for="group in apiGroups" :key="group.name">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <q-item class="q-pa-none q-mb-sm">
                                    <q-item-section avatar>
                                        <q-icon :name="group.icon" size="28px" color="primary" />
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label class="text-weight-bold" style="font-size:1.05rem;color:#0a0f1a;line-height:1.25;">{{ group.name }}</q-item-label>
                                    </q-item-section>
                                </q-item>
                                <div class="column q-gutter-xs">
                                    <div v-for="endpoint in group.endpoints" :key="endpoint" class="row no-wrap items-center">
                                        <code style="font-size:0.8rem;color:#555;background:rgba(0,0,0,0.04);padding:2px 6px;border-radius:4px;display:inline-block;max-width:100%;overflow-wrap:anywhere;">{{ endpoint }}</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Webhooks -->
            <section class="mkt-white reveal-fade-right" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container">
                    <div class="row q-col-gutter-xl items-center">
                        <div class="col-12 col-md-5">
                            <h2 class="text-weight-bold q-mb-sm" style="font-size:1.5rem;color:#0a0f1a;">24 Webhook Events</h2>
                            <p class="text-grey-7 q-mb-md" style="font-size:1rem;line-height:1.7;">
                                Real-time notifications when anything happens. All webhooks are signed with HMAC-SHA256
                                and include retry logic with delivery history.
                            </p>
                            <div class="column q-gutter-xs">
                                <div v-for="cat in webhookCategories" :key="cat.name">
                                    <div class="text-weight-bold q-mb-xs" style="font-size:0.85rem;color:#0a0f1a;">{{ cat.name }}</div>
                                    <div class="row q-gutter-xs q-mb-sm" style="flex-wrap:wrap;">
                                        <q-badge v-for="evt in cat.events" :key="evt" color="grey-3" text-color="grey-8"
                                            :label="evt" style="font-size:0.75rem;" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-md-7">
                            <div class="mkt-code-block">
<pre style="margin:0;white-space:pre;overflow-x:auto;"><span class="code-comment">// Example webhook payload: package.completed</span>
{
  <span class="code-string">"event"</span>: <span class="code-string">"package.completed"</span>,
  <span class="code-string">"timestamp"</span>: <span class="code-string">"2026-03-11T14:30:00Z"</span>,
  <span class="code-string">"data"</span>: {
    <span class="code-string">"packageId"</span>: <span class="code-string">"a1b2c3d4-..."</span>,
    <span class="code-string">"name"</span>: <span class="code-string">"Service Agreement"</span>,
    <span class="code-string">"status"</span>: <span class="code-string">"Completed"</span>,
    <span class="code-string">"completedAt"</span>: <span class="code-string">"2026-03-11T14:30:00Z"</span>,
    <span class="code-string">"signers"</span>: [
      { <span class="code-string">"email"</span>: <span class="code-string">"jane@example.com"</span>, <span class="code-string">"status"</span>: <span class="code-string">"Completed"</span> }
    ]
  }
}</pre>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Sandbox spotlight: visual lead-in to the Sandbox section. -->
            <marketing-spotlight
                slug="spot-sandbox"
                alt="eSign Launchpad sandbox included on every plan - real isolated environment for testing the API"
                eyebrow-icon="science"
                icon-circle-class="mkt-icon-circle--blue"
                title="Test the API before you pay"
                blurb="Every plan, including Free, ships with a real isolated sandbox. Same dashboard, same API, same flows - zero risk of touching production data or real money."
                :bullets="['Isolated tenant with its own data, branding, and API keys', 'Stripe routes to test mode automatically', 'Webhooks tagged with mode=test for router rules', 'One-click switch from your live account']"
                cta-label="See what is in the sandbox"
                cta-href="#sandbox"
                flip />

            <!-- ============================================================
                 SANDBOX
                 Stays before SDKs + Pricing on purpose: devs evaluate the
                 free-tier story BEFORE they look at price. The headline
                 promise - "every plan, no extra fee" - is the call-out we
                 want anchored in the reader's mind by the time they hit
                 the pricing card.
                 ============================================================ -->
            <section id="sandbox" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-xl">
                        <div class="mkt-section-eyebrow">Sandbox</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">
                            A full sandbox at every subscription level
                        </h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:620px;margin:0 auto;">
                            Build your integration against a real, isolated environment &mdash;
                            same dashboard, same API, same signing flows, zero risk of touching
                            production data or real money. Available on every plan, including the free tier.
                        </p>
                    </div>

                    <div class="row q-col-gutter-lg">
                        <!-- Left column: what the sandbox is -->
                        <div class="col-12 col-md-7">
                            <div class="mkt-card q-pa-lg" style="height:100%;">
                                <h3 class="text-weight-bold q-mb-md" style="font-size:1.15rem;color:#0a0f1a;">
                                    <q-icon name="science" color="primary" class="q-mr-xs" />
                                    What's in the box
                                </h3>
                                <q-list dense>
                                    <q-item v-for="item in sandboxFeatures" :key="item.title" class="q-pa-none q-mb-sm">
                                        <q-item-section avatar top style="min-width:26px;padding-right:0;">
                                            <q-icon name="check_circle" color="positive" size="20px" />
                                        </q-item-section>
                                        <q-item-section>
                                            <q-item-label class="text-weight-medium" style="color:#0a0f1a;">
                                                {{ item.title }}
                                            </q-item-label>
                                            <q-item-label caption class="text-grey-7">
                                                {{ item.detail }}
                                            </q-item-label>
                                        </q-item-section>
                                    </q-item>
                                </q-list>
                            </div>
                        </div>

                        <!-- Right column: pricing-tier callout + safety promises -->
                        <div class="col-12 col-md-5">
                            <div class="mkt-card q-pa-lg" style="height:100%;background:linear-gradient(135deg,#f0f4ff 0%,#e8f5ff 100%);border-color:#c8d4f0;">
                                <div class="text-overline text-primary q-mb-sm" style="line-height:1.2;">No extra charge</div>
                                <div class="text-weight-bold q-mb-md" style="font-size:1.4rem;color:#0a0f1a;line-height:1.2;">
                                    Every plan. Free tier included.
                                </div>
                                <p class="text-grey-8 q-mb-md" style="font-size:0.92rem;line-height:1.5;">
                                    Every paid feature is unlocked in the sandbox &mdash;
                                    embedded signing, signing groups, bulk send, signer payments,
                                    webhooks, identity verification &mdash; regardless of
                                    your live subscription tier.
                                </p>
                                <div class="text-overline text-grey-7 q-mt-md q-mb-xs" style="line-height:1.2;">Safety guarantees</div>
                                <ul class="q-pl-md q-mt-xs q-mb-none text-grey-8" style="font-size:0.88rem;line-height:1.55;">
                                    <li>Stripe charges route to test mode &mdash; never billed</li>
                                    <li>Documents watermarked <code style="background:rgba(0,0,0,0.05);padding:1px 4px;border-radius:3px;">TEST MODE</code></li>
                                    <li>No cryptographic signatures on sandbox PDFs</li>
                                    <li>IDV and KBA route to vendor sandbox APIs</li>
                                    <li>Reduced volume caps prevent accidental production use</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="text-center q-mt-xl">
                        <q-btn
                            unelevated
                            no-caps
                            color="primary"
                            label="Try the sandbox free"
                            size="md"
                            href="/Account/Register"
                            style="font-weight:600;"
                        />
                        <span class="text-caption text-grey-7 q-ml-md">
                            Sign up free &middot; click <strong>Switch to Sandbox</strong> in your developer settings
                        </span>
                    </div>
                </div>
            </section>

            <!-- SDKs & Documentation -->
            <section class="mkt-light reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-weight-bold q-mb-sm" style="font-size:1.5rem;color:#0a0f1a;">SDKs &amp; Documentation</h2>
                    <p class="text-grey-7 q-mb-xl" style="font-size:1rem;">Everything you need to integrate quickly</p>
                    <div class="row q-col-gutter-md justify-center">
                        <div class="col-6 col-md-3" v-for="resource in resources" :key="resource.title">
                            <div class="mkt-card q-pa-lg text-center" style="height:100%;">
                                <q-icon :name="resource.icon" size="36px" color="primary" class="q-mb-md" />
                                <h3 class="text-weight-bold q-mb-xs" style="font-size:1rem;color:#0a0f1a;">{{ resource.title }}</h3>
                                <p class="text-grey-7" style="font-size:0.85rem;">{{ resource.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- ============================================================
                 PRICING
                 ============================================================ -->
            <section id="pricing" class="mkt-white reveal-fade-up" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container-narrow">
                    <div class="text-center q-mb-lg">
                        <div class="mkt-section-eyebrow">Pricing</div>
                        <h2 class="text-weight-bold q-mb-sm" style="font-size:clamp(1.6rem,3vw,2.2rem);color:#0a0f1a;">Built for builders</h2>
                        <p class="text-grey-7" style="font-size:1rem;max-width:560px;margin:0 auto;">
                            $49/mo flat. 100 signings plus 100 API signings. Self-serve, no annual commitment.
                            $0.50/signing overage - pay only for what you send.
                        </p>
                    </div>
                    <div style="max-width:400px;margin:0 auto;">
                        <plan-card tier-code="developer"
                                   featured featured-label="For builders"
                                   icon="code" accented
                                   cta-label="Start Building"
                                   includes-label="What you get:" />
                    </div>
                    <div class="text-center q-mt-lg">
                        <p class="q-mb-sm" style="font-size:0.9rem;color:#475569;">
                            Scaling up? Pro includes 100 API signings plus full team workflow. Business adds SSO and 500 API signings.
                        </p>
                        <div class="row justify-center q-gutter-sm">
                            <q-btn flat no-caps dense color="primary" label="See Pro plan"
                                   icon-right="arrow_forward" href="/Home/SmallBusiness#plan-pro"
                                   style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                            <q-btn flat no-caps dense color="primary" label="See Business plan"
                                   icon-right="arrow_forward" href="/Home/SmallBusiness#plan-business"
                                   style="font-size:0.9rem;font-weight:500;border-radius:8px;" />
                        </div>
                    </div>
                </div>
            </section>

            <!-- CTA -->
            <section class="mkt-cta-gradient reveal-scale" v-intersection="revealCfg" style="padding:60px 0;">
                <div class="mkt-container text-center">
                    <h2 class="text-white text-weight-bold q-mb-md" style="font-size:1.8rem;">Ready to build?</h2>
                    <p class="text-white q-mb-lg" style="opacity:0.9;font-size:1rem;">Start free. Full API access on day one. No credit card until you upgrade.</p>
                    <div class="row justify-center q-gutter-md">
                        <q-btn color="white" text-color="deep-purple" size="lg" label="Start Building" href="/Account/Register?plan=developer"
                            no-caps unelevated style="border-radius:10px;padding:10px 36px;font-weight:600;" />
                        <q-btn outline color="white" size="lg" label="Read the Docs" href="/Docs/ApiReference"
                            no-caps style="border-radius:10px;padding:10px 36px;" />
                    </div>
                </div>
            </section>

            <marketing-footer />
        </q-page-container>
    </q-layout>
</template>

<script>
import PlanCard from '@components/marketing/PlanCard.vue';
import MarketingNav from '@components/marketing/MarketingNav.vue';
import MarketingFooter from '@components/marketing/MarketingFooter.vue';
import MarketingSpotlight from '@components/marketing/MarketingSpotlight.vue';
import { revealMixin } from '@util/reveal.js';

export default {
    name: 'DevelopersPage',
    mixins: [revealMixin],
    components: { PlanCard, MarketingNav, MarketingFooter, MarketingSpotlight },
    data() {
        return {
            mobileNav: false,

            // Use case cards - absorbed from ForDeveloper persona page.
            mcpBullets: [
                '90+ MCP tools, scoped per credential',
                'Same pricing as REST - agent calls billed identically',
                'Works with Claude Desktop, Cline, Continue, custom hosts'
            ],

            useCases: [
                { title: 'Indie SaaS & micro-SaaS', icon: 'rocket_launch', color: '#7C3AED',
                    detail: 'Embed signing in your SaaS. Users never leave your product. Self-serve from $49/mo.' },
                { title: 'AI agents & automation',  icon: 'auto_awesome',  color: '#047857',
                    detail: 'Claude drives signing through our MCP server. Upload, add signers, send - from a prompt.' },
                { title: 'ISVs embedding signing',  icon: 'extension',     color: '#4F46E5',
                    detail: 'Ship signing as a feature. REST API + webhooks + iframe. Self-serve, no annual commitment.' }
            ],

            quickStart: [
                { number: '1', title: 'Get a credential',  description: 'Sign up for Developer. Generate a Personal Access Token or OAuth client in Settings. One header to authenticate.' },
                { number: '2', title: 'Upload a document', description: 'POST a PDF or DOCX. We handle conversion + storage.' },
                { number: '3', title: 'Send for signature', description: 'Add signers, place fields, send. Recipients get a secure link.' }
            ],

            apiGroups: [
                { name: 'Packages', icon: 'inventory_2', endpoints: ['POST /packages', 'GET /packages', 'GET /packages/{id}', 'PUT /packages/{id}', 'POST /packages/{id}/send', 'POST /packages/{id}/void'] },
                { name: 'Documents', icon: 'description', endpoints: ['POST /documents/upload', 'GET /packages/{id}/documents', 'POST /documents/{id}/detect-tags', 'GET /documents/{id}/download'] },
                { name: 'Signers', icon: 'person', endpoints: ['POST /packages/{id}/signers', 'GET /packages/{id}/signers', 'PUT /signers/{id}', 'POST /signers/{id}/reassign'] },
                { name: 'Annotations', icon: 'edit_note', endpoints: ['POST /annotations', 'PUT /annotations/{id}', 'DELETE /annotations/{id}', 'POST /annotations/bulk'] },
                { name: 'Webhooks', icon: 'webhook', endpoints: ['POST /webhooks', 'GET /webhooks', 'POST /webhooks/{id}/test', 'GET /webhooks/{id}/deliveries'] },
                { name: 'Templates & More', icon: 'content_copy', endpoints: ['GET /templates', 'POST /templates/{id}/create-package', 'GET /signing-groups', 'GET /branding', 'POST /api-keys/rotate'] }
            ],

            webhookCategories: [
                { name: 'Package Events', events: ['package.created', 'package.sent', 'package.completed', 'package.declined', 'package.voided', 'package.expired'] },
                { name: 'Signer Events', events: ['signer.viewed', 'signer.completed', 'signer.declined', 'signer.authenticated', 'signer.reassigned'] },
                { name: 'Payment Events', events: ['payment.succeeded', 'payment.failed', 'payment.authorized', 'payment.batch_completed'] },
                { name: 'Document Events', events: ['document.uploaded', 'document.processed', 'document.processing_failed'] }
            ],

            resources: [
                { icon: 'menu_book', title: 'API Reference', description: 'Complete endpoint documentation with request/response examples' },
                { icon: 'integration_instructions', title: 'Integration Guides', description: 'Step-by-step guides for common integration patterns' },
                { icon: 'auto_awesome', title: 'MCP Server', description: 'Claude-native tool definitions for agent-driven signing workflows' },
                { icon: 'webhook', title: 'Webhook Testing', description: 'Built-in webhook tester in your dashboard settings' }
            ],

            // Sandbox-section bullets. Pairs with the Sandbox marketing
            // block before the SDKs section. Keep each detail one short
            // sentence - these render as q-item-label captions.
            sandboxFeatures: [
                {
                    title: 'A real, isolated environment',
                    detail: 'Separate database, branding, team, webhooks, API keys - everything a fresh tenant has, paired to your live account.'
                },
                {
                    title: 'Same dashboard, same API, same flows',
                    detail: 'Identical surfaces to production so anything you build in the sandbox works in live without code changes.'
                },
                {
                    title: 'One-click switch from your live account',
                    detail: 'Use the Switch to Sandbox button in Developer Settings. Exit the same way to return to live without re-authenticating.'
                },
                {
                    title: 'Webhook deliveries tagged for routing',
                    detail: 'Every sandbox webhook body carries "mode":"test" and an X-eSign-Mode header so receivers can route without parsing payloads.'
                },
                {
                    title: 'Production-quality observability',
                    detail: 'Delivery history, retry controls, signing secret rotation, and event payload inspection - all available in the sandbox UI.'
                }
            ]
        };
    }
};
</script>

<style scoped>
.mkt-section-eyebrow {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #4F46E5;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
}
</style>
